import React, { useEffect, useState } from 'react';
import { BlueprintVersion } from '@/lib/stores/storeTypes';
import { apiService } from '@/lib/api-functions/api-service';
import { VersionCard } from './VersionCard';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConfirmation } from '@/hooks/use-confirmation';
import { useToast } from '@/hooks/use-toast';
import { formatVersion } from '@/lib/utils/versionUtils';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface VersionHistoryPanelProps {
  blueprintId: string;
  majorVersion?: number;
  onRestore?: () => void; // Callback when restore is successful
  currentVersion?: { major: number; minor: number; patch: number };
}

export function VersionHistoryPanel({ blueprintId, majorVersion, onRestore, currentVersion }: VersionHistoryPanelProps) {
  const { t } = usePlatformTranslation();
  const [versions, setVersions] = useState<BlueprintVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const limit = 20;
  const { confirmation } = useConfirmation();
  const { toast } = useToast();

  const fetchVersions = async (append: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.fetchBlueprintVersions(
        blueprintId,
        majorVersion,
        limit,
        append ? offset : 0
      );

      if (response.success && response.data) {
        const newVersions = response.data;
        setVersions(append ? [...versions, ...newVersions] : newVersions);
        setHasMore(newVersions.length === limit);
        setOffset(append ? offset + newVersions.length : newVersions.length);
      } else {
        setError(response.error || 'Failed to load version history');
      }
    } catch (err) {
      setError('Failed to load version history');
      console.error('Version fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (blueprintId) {
      // Auto-sync items with version mismatch (no structural changes)
      autoSyncItems();
      // Then fetch version history
      fetchVersions(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blueprintId, majorVersion]);

  const autoSyncItems = async () => {
    try {
      console.log('[AUTO-SYNC] Checking for items to auto-sync...');
      const response = await apiService.autoSyncBlueprintItems(blueprintId);
      
      if (response.success && response.data) {
        const { syncedItems, canAutoSync } = response.data;
        
        if (syncedItems > 0) {
          toast({
            title: "Items Auto-Synced",
            description: `${syncedItems} item${syncedItems !== 1 ? 's' : ''} automatically updated to the latest version.`,
            variant: "default",
          });
          console.log(`[AUTO-SYNC] ✓ ${syncedItems} items synced`);
        } else if (canAutoSync === false) {
          console.log('[AUTO-SYNC] Cannot auto-sync: version has breaking/review changes');
        }
      }
    } catch (error) {
      console.error('[AUTO-SYNC] Error:', error);
      // Don't show error toast, it's a background operation
    }
  };

  const handleLoadMore = () => {
    fetchVersions(true);
  };

  const handleView = (version: BlueprintVersion) => {
    // TODO: Open modal to view version details
    console.log('View version:', version);
  };

  const handleRestore = async (version: BlueprintVersion) => {
    const versionString = formatVersion({
      major: version.major_version,
      minor: version.minor_version,
      patch: version.patch_version
    });

    const confirmDialog = confirmation({
      title: 'Restore Blueprint Version',
      description: `Are you sure you want to restore this template to v${versionString}? This will create a new version with the restored data. Your current version will be preserved in history.`,
      confirmText: 'Restore',
      cancelText: 'Cancel',
      variant: 'default',
      onConfirm: async () => {
        setRestoringId(version.id);
        
        try {
          const response = await apiService.restoreBlueprintVersion(blueprintId, version.id);
          
          if (response.success) {
            toast({
              title: 'Success',
              description: `Template restored to v${versionString}`,
            });
            
            // Refresh version history
            await fetchVersions(false);
            
            // Call parent callback to refresh the blueprint
            onRestore?.();
            
            confirmDialog.dismiss();
          } else {
            throw new Error(response.error || 'Failed to restore version');
          }
        } catch (err) {
          console.error('Restore error:', err);
          toast({
            title: 'Error',
            description: err instanceof Error ? err.message : 'Failed to restore version',
            variant: 'destructive',
          });
        } finally {
          setRestoringId(null);
        }
      },
      onCancel: () => {
        confirmDialog.dismiss();
      },
    });
  };

  if (loading && versions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
        <p className="text-sm text-gray-600">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => fetchVersions(false)}
          className="mt-4"
        >
          Try Again
        </Button>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-gray-500">{t('platform.blueprint-version.versionhistorypanel.no_version_history_available')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {versions.map((version, index) => (
        <VersionCard
          key={version.id}
          version={version}
          previousVersion={index < versions.length - 1 ? versions[index + 1] : undefined}
          onView={handleView}
          onRestore={handleRestore}
          isRestoring={restoringId === version.id}
          currentVersion={currentVersion}
          onRefresh={() => fetchVersions(false)}
        />
      ))}

      {hasMore && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleLoadMore}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </>
            ) : (
              'Load More'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

